// The two queries behind voice's get_schedule — issue #1686.
//
// Split from voiceSchedule.ts on the same line createEventWrite.ts is split from
// createEventBuilder.ts: everything with a `client` in it lives here, so the
// shaping and the rules stay coverable without a database. The client is
// INJECTED rather than imported, which is what lets the real queries be driven
// against a fake in vitest — the #1670 tests do exactly this.
//
// ── Which rows count as "on her calendar" ────────────────────────────────────
//
// Three filters, and none of them are invented here:
//
//   status = 'active'         every reader in the app and both dispatcher reads
//   is_dismissed = false      she swiped it off her week; it is gone from her
//                             morning note and her Apple feed, so it is gone
//                             from what Kin says out loud
//   category is null | ≠ task a row with category 'task' is a to-do, not a
//                             calendar entry (kinFeedRows.isFeedRow and
//                             dayCardPartition.ts both draw this line)
//
// Voice matches the MORNING NOTE here, because that is the other place Kin says
// her day out loud, and a mom who dismissed something does not want to hear it
// read back. dispatch-notifications (the morning note), kin-calendar-feed,
// TasksTab and kinFeedRows draw the same line.
//
// ⚠️ THIS COMMENT USED TO NAME kin-chat's buildAmbientCalendarContext AS AN
// OUTLIER that filtered only on status, and promised the fix as "its own issue".
// Both halves are now false: #1696 put that block on `onlyVisibleEvents` (which
// adds `is_dismissed = false` beside the status filter) and the issue is closed.
// Corrected rather than deleted, because a comment promising an unfiled issue
// for a closed bug sends the next reader to file a duplicate or to "fix" what is
// already fixed. There is no outlier left among these readers.
//
// ── What is NOT read ─────────────────────────────────────────────────────────
//
// Undated Ready-to-Fire cards. A draft with `send_by` null is the "Whenever"
// bucket (#1028) — real, and deliberately not attached to a day. Reading it into
// an answer about today would put a thing with no deadline into her day, which
// is the opposite of what she asked.

import {
  buildVoiceScheduleResult,
  resolveVoiceScheduleWindow,
  type VoiceScheduleEventRow,
  type VoiceScheduleRange,
  type VoiceScheduleResult,
  type VoiceScheduleTaskRow,
} from "./voiceSchedule";
// The same PostgREST filter kin-calendar-feed uses, imported rather than
// hand-copied: it IS the "what counts as an event" rule, and a second copy of it
// is how the two surfaces would come to disagree about whether a to-do is a
// calendar entry.
import { FEED_EVENT_CATEGORY_FILTER } from "../../supabase/functions/_shared/kinFeedRows";
import { onlyVisibleEvents } from "../../supabase/functions/_shared/eventVisibility";
// Her timezone, resolved exactly the way the WRITE resolves it. Load-bearing:
// "put it on for tomorrow" and "what's on tomorrow" have to mean the same day,
// and they only do if both go through the same resolver.
//
// ⚠️ Not the same resolver as the date Kin GREETS her with — that one comes from
// the user_cap_timezone() RPC (kin-voice-session/spokenDate.ts), because it has
// to agree with the SQL that decides her voice cap's day. Both prefer her
// configured zone and both fall back to America/New_York, so they agree in every
// case either has been observed in; they are two resolvers because they answer
// to two different authorities, which is worth knowing before "unifying" them.
import { userTimeZone } from "../../supabase/functions/kin-chat/createEventWrite";
import { todayYmdInZone } from "../../supabase/functions/kin-chat/createEventBuilder";

export interface VoiceScheduleClient {
  from: (table: string) => any;
}

/**
 * Read more than can be spoken, on purpose.
 *
 * voiceSchedule's caps (12 events, 8 tasks) decide what the model is GIVEN; this
 * decides what is fetched. Fetching exactly the cap would make `more_events`
 * permanently zero, so Kin could never tell her she has a fuller week than the
 * three things she just heard — the cap would silently become the truth.
 */
export const SCHEDULE_FETCH_LIMIT = 60;

/** A failure, phrased so the model can say it and carry on. Never thrown. */
export const SCHEDULE_READ_FAILED =
  "You could not reach her calendar just now — this is a problem on your end, not an empty calendar. " +
  "Tell her you couldn't pull it up this second and offer to try again. " +
  "⚠️ Do NOT say she has nothing on, and do NOT say you can't see her calendar at all: you normally can.";

export type VoiceScheduleOutcome =
  | VoiceScheduleResult
  | { ok: false; error: string };

/**
 * Her events and her dated Ready-to-Fire cards for the window, shaped for speech.
 *
 * ⚠️ Never throws and never rejects. A tool handler that throws leaves the model
 * waiting on a function_call_output that is not coming and Kin goes silent
 * mid-call — see the header of voiceWriteBudget.ts. Silence is how a voice
 * assistant reads as broken; a spoken "I couldn't pull it up" does not.
 *
 * ⚠️ A read failure is reported as a FAILURE, never as an empty calendar. Those
 * are opposite answers and only one of them is safe to be wrong about: "you're
 * clear today" over an unreadable calendar is the false-confirmation shape of
 * house rule 16, aimed at her day instead of at a write.
 */
export async function readVoiceSchedule(
  client: VoiceScheduleClient,
  userId: string,
  range: VoiceScheduleRange,
  now: Date = new Date(),
): Promise<VoiceScheduleOutcome> {
  try {
    const tz = await userTimeZone(client as any, userId);
    const todayYmd = todayYmdInZone(now, tz);
    const window = resolveVoiceScheduleWindow(range, todayYmd);

    // #1696 — this reader was one of only two that were already right, and it
    // moves onto the shared rule for the same reason the other did: a correct
    // hand-written copy is still a copy, and six of the eight readers of this
    // table prove what happens to copies of this rule.
    // #1706 — DECISION: voice MARKS a completed event, it does not hide it. She
    // asked out loud; "you had the dentist at nine, that's done" is the answer.
    // Hiding it would make Kin silent about her own morning in the one mode where
    // she cannot go and look. Her 7am note and prep offers exclude instead — the
    // per-surface record is in _shared/eventVisibility.ts.
    //
    // ⚠️ `is_completed` must stay in this select list or `buildVoiceScheduleResult`
    // marks nothing and voice silently goes back to speaking a done event as
    // pending. Pinned by COMPLETED_DECISIONS in eventVisibilityCoverage.test.ts.
    const { data: eventData, error: eventError } = await onlyVisibleEvents(
      client
        .from("events")
        .select("title,event_date,start_time,is_all_day,location,child,is_completed"),
    )
      .eq("user_id", userId)
      .or(FEED_EVENT_CATEGORY_FILTER)
      .gte("event_date", window.startYmd)
      .lte("event_date", window.endYmd)
      .order("event_date", { ascending: true })
      .order("start_time", { ascending: true, nullsFirst: true })
      .limit(SCHEDULE_FETCH_LIMIT);
    if (eventError) {
      console.error("[get_schedule] events query error:", eventError);
      return { ok: false, error: SCHEDULE_READ_FAILED };
    }

    // ⚠️ A failed TASKS read does NOT fail the whole answer. Her calendar is the
    // thing she asked about; losing the card list degrades the answer, while
    // refusing to answer at all over it loses her the part that did come back.
    // The opposite lean from the events read above, and deliberately so.
    //
    // ⚠️ But the failure is CARRIED, not swallowed (Copilot, #1695). Collapsing
    // it to an empty array made an unreadable Tasks tab indistinguishable from
    // an empty one — and with no events either, the answer became a confident
    // "you're completely clear" over a table nobody could read. That is the same
    // defect the events branch above refuses to make, landing one query later.
    //
    // ⚠️ And it is wrapped in its OWN try/catch. Without one a REJECTED task
    // query fell through to the outer catch and turned the whole answer into a
    // calendar failure — the partial-failure contract stated two paragraphs up,
    // silently not honoured for the rejection case.
    let taskData: VoiceScheduleTaskRow[] = [];
    let tasksUnreadable = false;
    try {
      const { data: rawTasks, error: taskError } = await client
        .from("task_queue_items")
        .select("title,action_type,send_by")
        .eq("user_id", userId)
        .eq("status", "draft")
        .gte("send_by", window.startYmd)
        .lte("send_by", window.endYmd)
        .order("send_by", { ascending: true })
        .limit(SCHEDULE_FETCH_LIMIT);
      if (taskError) {
        console.error("[get_schedule] task_queue_items query error:", taskError);
        tasksUnreadable = true;
      } else if (Array.isArray(rawTasks)) {
        taskData = rawTasks as VoiceScheduleTaskRow[];
      } else {
        // A 2xx with no array is not an empty list either — it is a shape
        // nobody expected, and claiming her Tasks tab is clear off the back of
        // it is the same overclaim.
        tasksUnreadable = true;
      }
    } catch (e) {
      console.error("[get_schedule] task_queue_items read threw:", e);
      tasksUnreadable = true;
    }

    return buildVoiceScheduleResult({
      range,
      todayYmd,
      events: (Array.isArray(eventData) ? eventData : []) as VoiceScheduleEventRow[],
      tasks: taskData,
      tasksUnreadable,
    });
  } catch (e) {
    console.error("[get_schedule] read threw:", e);
    return { ok: false, error: SCHEDULE_READ_FAILED };
  }
}
